import React, { useState } from "react";
import { ClipboardList, Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";
import { formatTime12h, getTodayString } from "../eventTime";

const LOCATION_FILTERS = ["Forum", "Studio 214", "ALL"];
const matchesLocation = (e, filter) => {
  if (filter === "ALL") return true;
  const loc = (e.location || "").toLowerCase();
  if (filter === "Forum") return loc.includes("forum");
  if (filter === "Studio 214") return loc.includes("214") || loc.includes("studio 214");
  return true;
};

const SignupRow = ({ event, onSave }) => {
  const [limit, setLimit] = useState(event.signup_limit ?? "");
  const open = event.signup_open !== false;
  const count = event.signup_count ?? 0;
  const lim = event.signup_limit;
  const full = lim != null && count >= lim;

  return (
    <tr className="border-b border-zinc-800/50 hover:bg-zinc-900" data-testid={`signup-row-${event.id}`}>
      <td className="py-3 px-3">
        <div className="font-bold text-white">{event.name}</div>
        <div className="text-xs text-zinc-500">{event.location || "—"}</div>
        {(event.livestream_youtube || event.recording_required) && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {event.livestream_youtube && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">● LIVESTREAM</span>
            )}
            {event.recording_required && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">● RECORDING</span>
            )}
          </div>
        )}
      </td>
      <td className="py-3 px-3 font-mono text-zinc-200">{event.date}</td>
      <td className="py-3 px-3 font-mono text-crew-yellow">{formatTime12h(event.call_time)}</td>
      <td className="py-3 px-3 text-center">
        <span className={`font-bold ${full ? "text-red-400" : "text-white"}`} data-testid={`signup-count-${event.id}`}>
          {count}{lim != null ? ` / ${lim}` : " / ∞"}
        </span>
      </td>
      <td className="py-3 px-3">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="0"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="∞"
            className="bg-black border-zinc-700 text-white w-20 h-8"
            data-testid={`signup-limit-input-${event.id}`}
          />
          <Button size="sm" onClick={() => onSave(event.id, { signup_limit: limit === "" ? null : Number(limit) })}
            className="bg-crew-yellow text-black font-bold hover:bg-white h-8" data-testid={`signup-limit-save-${event.id}`}>
            Set
          </Button>
        </div>
      </td>
      <td className="py-3 px-3 text-center">
        <Button
          size="sm"
          onClick={() => onSave(event.id, { signup_open: !open })}
          className={`h-8 font-bold uppercase ${open ? "bg-green-600 hover:bg-green-700 text-white" : "bg-zinc-700 hover:bg-zinc-600 text-white"}`}
          data-testid={`signup-toggle-${event.id}`}
        >
          {open ? <><Unlock className="h-3 w-3 mr-1" /> Open</> : <><Lock className="h-3 w-3 mr-1" /> Closed</>}
        </Button>
      </td>
    </tr>
  );
};

export const SignupsTab = (props) => {
  const { events, handleUpdateSignupSettings, crewVisibility, handleSetCrewVisibility } = props;
  const [locationFilter, setLocationFilter] = useState("Forum");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const today = getTodayString();

  const rows = (events || [])
    .filter((e) => e.status !== "cancelled")
    .filter((e) => matchesLocation(e, locationFilter))
    .filter((e) => {
      // default: upcoming; plus optional date range
      if (startDate && (!e.date || e.date < startDate)) return false;
      if (endDate && (!e.date || e.date > endDate)) return false;
      if (!startDate && !endDate) return e.date && e.date >= today;
      return true;
    })
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  return (
    <TabsContent value="signups" data-testid="signups-content">
      <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-crew-yellow" />
            <h2 className="text-2xl font-bold text-crew-yellow">CREW CALL SIGN-UPS</h2>
          </div>
          <p className="text-zinc-400 text-sm mt-1">
            Control which events crew can sign up for. Set a capacity limit and open/close each event.
            Crew only see Open/Closed — never the numbers. {rows.length} events shown.
          </p>
          <div className="mt-3 p-3 rounded-sm bg-black/40 border border-crew-yellow/30 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase font-bold text-crew-yellow">Crew can see locations:</span>
            <Select value={crewVisibility || "Forum"} onValueChange={handleSetCrewVisibility}>
              <SelectTrigger className="bg-black border-zinc-700 text-white w-40 h-9" data-testid="crew-visibility-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                {LOCATION_FILTERS.map((l) => (
                  <SelectItem key={l} value={l} className="text-white hover:bg-zinc-800">{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-zinc-500">Crew members only see events in the selected location(s). Default: Forum.</span>
          </div>
          <div className="flex flex-wrap items-end gap-3 mt-4">
            <div>
              <Label className="text-xs text-zinc-500 uppercase font-bold">Location</Label>
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger className="bg-black border-zinc-700 text-white w-36 h-9 mt-1" data-testid="signups-location-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  {LOCATION_FILTERS.map((l) => (
                    <SelectItem key={l} value={l} className="text-white hover:bg-zinc-800">{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase font-bold">From</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="bg-black border-zinc-700 text-white h-9 mt-1" data-testid="signups-start-date" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase font-bold">To</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="bg-black border-zinc-700 text-white h-9 mt-1" data-testid="signups-end-date" />
            </div>
            {(startDate || endDate) && (
              <Button variant="outline" onClick={() => { setStartDate(""); setEndDate(""); }}
                className="border-zinc-700 text-zinc-300 h-9" data-testid="signups-clear-dates">
                Clear dates (upcoming)
              </Button>
            )}
          </div>
        </div>

        <div className="p-4">
          {rows.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="no-signup-events">No events match the current filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-zinc-400 border-b border-zinc-800 bg-zinc-900">
                  <tr>
                    <th className="py-3 px-3">Event</th>
                    <th className="py-3 px-3">Date</th>
                    <th className="py-3 px-3">Crew Call</th>
                    <th className="py-3 px-3 text-center">Signed Up</th>
                    <th className="py-3 px-3">Limit (admin only)</th>
                    <th className="py-3 px-3 text-center">Sign-ups</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <SignupRow key={e.id} event={e} onSave={handleUpdateSignupSettings} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </TabsContent>
  );
};
